import { z } from 'zod';
import { vpnLinkSchema } from './vpn.js';

/**
 * A resource is anything the platform monitors: a firewall, a VPN tunnel, a
 * workstation, a SIP trunk, an internet line, a customer account...
 *
 * The core knows nothing about the *kind* of resource. Plugins declare kinds,
 * own the collection logic, and the core stores/streams/alerts on the result.
 */

export const HEALTH_STATES = ['up', 'degraded', 'down', 'maintenance', 'unknown'] as const;
export type HealthState = (typeof HEALTH_STATES)[number];

/** Ordered worst-to-best so we can roll child health up into a parent. */
export const HEALTH_SEVERITY: Record<HealthState, number> = {
  down: 4,
  degraded: 3,
  unknown: 2,
  maintenance: 1,
  up: 0,
};

/**
 * The separate problems inside one status message. Collectors join theirs
 * with "; " ("nginx not running; disk 91% full"), so each part can be ignored
 * on its own.
 */
export function splitNotices(message: string | null | undefined): string[] {
  return (message ?? '')
    .split(/;\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Numbers wildcarded, so ignoring "disk 91% full" also covers "disk 93% full" next poll. */
export function noticeKey(notice: string): string {
  return notice.toLowerCase().replace(/\d+(?:\.\d+)?/g, '#').replace(/\s+/g, ' ').trim();
}

export function isNoticeIgnored(notice: string, ignored: readonly string[]): boolean {
  const key = noticeKey(notice);
  return ignored.some((i) => noticeKey(i) === key);
}

/**
 * Lift a `degraded` or `down` result to `up` when every notice behind it has
 * been ignored — the operator has said this state is expected (a box that is
 * powered off on purpose, a disk that is meant to be full). `unknown` is never
 * lifted: it means "we could not tell", which ignoring cannot answer. The
 * message is kept as-is so the ignored notices stay visible (and can be
 * un-ignored) rather than silently disappearing.
 */
export function applyIgnoredNotices(
  health: HealthState,
  statusMessage: string | null,
  ignored: readonly string[],
): HealthState {
  if ((health !== 'degraded' && health !== 'down') || ignored.length === 0) return health;
  const notices = splitNotices(statusMessage);
  if (notices.length === 0) return health;
  return notices.every((n) => isNoticeIgnored(n, ignored)) ? 'up' : health;
}

/** The resources whose health counts towards a parent, site or client rollup. */
export function rollupMembers<T extends { ignoreHealthRollup?: boolean }>(resources: T[]): T[] {
  return resources.filter((r) => !r.ignoreHealthRollup);
}

export function rollUpHealth(states: HealthState[]): HealthState {
  if (states.length === 0) return 'unknown';
  return states.reduce((worst, s) => (HEALTH_SEVERITY[s] > HEALTH_SEVERITY[worst] ? s : worst), 'up');
}

/**
 * One entry in a resource's secondary-address or MAC-address list.
 *
 * Labeled rather than a bare string list — "10.0.1.5" means nothing on a
 * device with three interfaces, but "WAN · 10.0.1.5" does.
 */
export const addressEntrySchema = z.object({
  label: z.string().trim().max(60).default(''),
  value: z.string().trim().min(1).max(500),
});
export type AddressEntry = z.infer<typeof addressEntrySchema>;

export const resourceSchema = z.object({
  id: z.string(),
  /** Plugin that owns this resource, e.g. "firewalls". */
  pluginId: z.string(),
  /** Kind within the plugin, e.g. "fortigate" or "sip-trunk". */
  kind: z.string(),
  name: z.string().min(1),
  /** Optional parent for tree views (site -> rack -> device). */
  parentId: z.string().nullable().default(null),
  /** Tenant / customer this resource belongs to. null = internal / shared. */
  customerId: z.string().nullable().default(null),
  siteId: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  /** Other addresses this device answers on — a second NIC, a public IP, a
   *  status page URL. Diagnostics and the Access card still target `address`
   *  alone; these are inventory, for a human to read. */
  secondaryAddresses: z.array(addressEntrySchema).default([]),
  /** Known MAC addresses, one per interface where more than one is tracked. */
  macAddresses: z.array(addressEntrySchema).default([]),
  /** Tunnels recorded by hand — drawn as links on the network map alongside
   *  the ones SSH/SNMP discovery finds. */
  vpnLinks: z.array(vpnLinkSchema).max(50).default([]),
  tags: z.array(z.string()).default([]),
  health: z.enum(HEALTH_STATES).default('unknown'),
  /** Human readable reason for the current health state. */
  statusMessage: z.string().nullable().default(null),
  /** Free-form, plugin-owned. Rendered by the plugin's own detail view. */
  attributes: z.record(z.unknown()).default({}),
  /** Connection settings; secret fields are encrypted at rest by the core. */
  config: z.record(z.unknown()).default({}),
  enabled: z.boolean().default(true),
  /** Keep this resource's health out of every rollup above it — its parent
   *  resource, its site and its client — while still monitoring and alerting
   *  on it. For a noisy or non-critical device that shouldn't paint the whole
   *  site red. */
  ignoreHealthRollup: z.boolean().default(false),
  /** Degradation notices the operator has accepted, as the collector worded
   *  them ("disk 91% full", "Host unreachable"). When every notice behind a
   *  `degraded` or `down` result is on this list the resource reads `up`; see
   *  `applyIgnoredNotices`. */
  ignoredNotices: z.array(z.string().trim().min(1).max(300)).max(50).default([]),
  /** Seconds between collection runs. Falls back to the plugin default. */
  pollIntervalSec: z.number().int().positive().nullable().default(null),
  lastCheckedAt: z.number().nullable().default(null),
  lastChangedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});

export type Resource = z.infer<typeof resourceSchema>;

export const resourceCreateSchema = resourceSchema
  .omit({ id: true, createdAt: true, updatedAt: true, lastCheckedAt: true, lastChangedAt: true })
  .partial({
    parentId: true,
    customerId: true,
    siteId: true,
    address: true,
    secondaryAddresses: true,
    macAddresses: true,
    vpnLinks: true,
    tags: true,
    health: true,
    statusMessage: true,
    attributes: true,
    config: true,
    enabled: true,
    ignoreHealthRollup: true,
    ignoredNotices: true,
    pollIntervalSec: true,
  });
export type ResourceCreate = z.infer<typeof resourceCreateSchema>;

export const resourceUpdateSchema = resourceCreateSchema.partial();
export type ResourceUpdate = z.infer<typeof resourceUpdateSchema>;

export const resourceQuerySchema = z.object({
  pluginId: z.string().optional(),
  kind: z.string().optional(),
  customerId: z.string().optional(),
  siteId: z.string().optional(),
  health: z.enum(HEALTH_STATES).optional(),
  tag: z.string().optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ResourceQuery = z.infer<typeof resourceQuerySchema>;

/**
 * A resource this server cannot reach directly, that a nearby MikroTik still
 * does: while this is set, monitoring, diagnostics and every access path go
 * through that MikroTik, and the server keeps trying the direct path on each
 * check — the mark is dropped the moment the device answers directly again.
 * Kept in `attributes.escrow` so it survives restarts and travels with the
 * resource's realtime frames.
 */
export interface ResourceEscrow {
  relayId: string;
  relayName: string;
  /** The relay's own address — the one this server connects to. */
  relayAddress: string;
  since: number;
  /** What the relay proved when the device went into escrow: "ping 3/3", "ssh port 22 via dst-nat" … */
  evidence: string[];
  /** When the relay is in escrow itself: the relays behind it, nearest first, that the traffic also passes through. */
  through?: Array<{ name: string; address: string }>;
}

export function escrowOf(resource: { attributes?: Record<string, unknown> | null }): ResourceEscrow | null {
  const value = resource.attributes?.escrow as Partial<ResourceEscrow> | null | undefined;
  if (!value || typeof value !== 'object' || typeof value.relayId !== 'string' || typeof value.relayAddress !== 'string') return null;
  return {
    relayId: value.relayId,
    relayName: typeof value.relayName === 'string' ? value.relayName : value.relayAddress,
    relayAddress: value.relayAddress,
    since: typeof value.since === 'number' ? value.since : 0,
    evidence: Array.isArray(value.evidence) ? value.evidence.filter((e): e is string => typeof e === 'string') : [],
    ...(Array.isArray(value.through) && value.through.length
      ? { through: value.through.filter((t) => t && typeof t.address === 'string').map((t) => ({ name: String(t.name ?? t.address), address: t.address })) }
      : {}),
  };
}

/** The line a resource in escrow carries in its status, so every page that shows the status says so. */
export function escrowNotice(escrow: ResourceEscrow): string {
  const nested = escrow.through?.length ? `, itself reached through ${escrow.through.map((t) => t.address).join(' then ')}` : '';
  return `Escrow through ${escrow.relayAddress} (MikroTik "${escrow.relayName}"${nested}) — not reachable directly from this server, so checks and access go through it until it answers directly again`;
}
